/**
 * Workbook data model — plain immutable structures + pure operations. Adapted from SolveSheets:
 * cells are strings (what the user typed / the file's formatted text), every operation returns a
 * NEW rows array with structural sharing, so undo can hold plain `rows` references.
 *
 * Generic on purpose: the contact importer is one consumer; anything that previews or edits
 * tabular data (exports, reports, future uploads) can reuse it.
 */

export interface SheetData {
  id: string;
  name: string;
  /** Ragged row-major cells; absent/short rows read as ''. */
  rows: string[][];
  /** Per-column pixel widths; undefined = DEFAULT_COL_W. */
  colWidths: (number | undefined)[];
  /** Freeze-panes column count — columns [0, pinnedCols) stick to the left. */
  pinnedCols: number;
  /** Row 1 is a header: bold, sticky under the letter row, excluded from sort. */
  frozenHeader: boolean;
  /** Per-column value filter (data-col index → allowed values). View-only. */
  filters: Record<number, string[]>;
}

export type DocKind = 'xlsx' | 'csv';

export interface SheetDoc {
  id: string;
  /** Display / file name WITHOUT extension. */
  name: string;
  kind: DocKind;
  sheets: SheetData[];
  activeSheet: number;
  dirty: boolean;
  /** Where it came from, for tooltips ("Local file", "Import"). */
  sourceLabel: string;
}

export const DEFAULT_COL_W = 112;
export const MIN_COL_W = 40;
export const MAX_COL_W = 640;
export const ROW_H = 28;
export const HEADER_H = 26;
export const ROW_NUM_W = 48;
/** Blank rows/cols shown past the data so the sheet feels open-ended (editable mode only). */
export const EXTRA_ROWS = 20;
export const EXTRA_COLS = 3;
export const MIN_COLS = 8;
/** Parse guard — sheets larger than this are truncated with a warning. */
export const MAX_PARSE_CELLS = 400_000;

let idCounter = 0;
export const nextId = (prefix: string) => `${prefix}-${++idCounter}`;

// ── addressing ──────────────────────────────────────────────────────────────

/** 0 → A, 25 → Z, 26 → AA … (bijective base 26). */
export function colLabel(c: number): string {
  let label = '';
  for (let n = c; n >= 0; n = Math.floor(n / 26) - 1) {
    label = String.fromCharCode(65 + (n % 26)) + label;
  }
  return label;
}

export function cellRef(r: number, c: number): string {
  return `${colLabel(c)}${r + 1}`;
}

// ── constructors ────────────────────────────────────────────────────────────

export function emptySheet(name: string): SheetData {
  return { id: nextId('sheet'), name, rows: [], colWidths: [], pinnedCols: 0, frozenHeader: false, filters: {} };
}

export function sheetFromRows(name: string, rows: string[][], opts: { frozenHeader?: boolean } = {}): SheetData {
  return { id: nextId('sheet'), name, rows, colWidths: [], pinnedCols: 0, frozenHeader: opts.frozenHeader ?? rows.length > 1, filters: {} };
}

// ── reads ───────────────────────────────────────────────────────────────────

export function getCell(sheet: SheetData, r: number, c: number): string {
  return sheet.rows[r]?.[c] ?? '';
}

export function dataColCount(sheet: SheetData): number {
  let max = 0;
  for (const row of sheet.rows) if (row.length > max) max = row.length;
  return max;
}

/** Grid-visible dimensions: data + optional open-ended blank space. */
export function displayDims(sheet: SheetData, filtered: boolean, openEnded: boolean): { rows: number; cols: number } {
  return {
    rows: sheet.rows.length + (filtered || !openEnded ? 0 : EXTRA_ROWS),
    cols: Math.max(dataColCount(sheet) + (openEnded ? EXTRA_COLS : 0), openEnded ? MIN_COLS : 1),
  };
}

export function colWidth(sheet: SheetData, c: number): number {
  return sheet.colWidths[c] ?? DEFAULT_COL_W;
}

/** Data-row indices that pass the sheet's column filters (header row always passes). */
export function visibleDataRows(sheet: SheetData): number[] | null {
  const cols = Object.keys(sheet.filters).map(Number);
  if (cols.length === 0) return null; // null = identity, no filter
  const allowed = new Map(cols.map((c) => [c, new Set(sheet.filters[c])]));
  const out: number[] = [];
  for (let r = 0; r < sheet.rows.length; r++) {
    if (r === 0 && sheet.frozenHeader) {
      out.push(r);
      continue;
    }
    let pass = true;
    for (const [c, set] of allowed) {
      if (!set.has(getCell(sheet, r, c))) {
        pass = false;
        break;
      }
    }
    if (pass) out.push(r);
  }
  return out;
}

// ── immutable operations ────────────────────────────────────────────────────

/** Set one cell, growing the grid as needed. */
export function setCell(sheet: SheetData, r: number, c: number, value: string): SheetData {
  return setCells(sheet, [[r, c, value]]);
}

/** Batch cell write (paste, delete-range, fill). One undo entry. */
export function setCells(sheet: SheetData, writes: [number, number, string][]): SheetData {
  if (writes.length === 0) return sheet;
  const rows = sheet.rows.slice();
  const touched = new Set<number>();
  let maxRow = rows.length - 1;
  for (const [r] of writes) if (r > maxRow) maxRow = r;
  for (let r = rows.length; r <= maxRow; r++) rows[r] = [];
  for (const [r, c, value] of writes) {
    if (!touched.has(r)) {
      rows[r] = (rows[r] ?? []).slice();
      touched.add(r);
    }
    const row = rows[r] as string[];
    for (let i = row.length; i < c; i++) row[i] = '';
    row[c] = value;
  }
  return { ...sheet, rows };
}

export function insertRows(sheet: SheetData, at: number, count: number): SheetData {
  const rows = sheet.rows.slice();
  const blanks: string[][] = Array.from({ length: count }, () => []);
  rows.splice(Math.min(at, rows.length), 0, ...blanks);
  return { ...sheet, rows };
}

export function deleteRows(sheet: SheetData, at: number, count: number): SheetData {
  const rows = sheet.rows.slice();
  rows.splice(at, count);
  return { ...sheet, rows, filters: {} };
}

export function deleteRowSet(sheet: SheetData, indices: Set<number>): SheetData {
  return { ...sheet, rows: sheet.rows.filter((_, i) => !indices.has(i)), filters: {} };
}

export function insertCols(sheet: SheetData, at: number, count: number): SheetData {
  const rows = sheet.rows.map((row) => {
    if (row.length <= at) return row;
    const next = row.slice();
    next.splice(at, 0, ...Array.from({ length: count }, () => ''));
    return next;
  });
  const colWidths = sheet.colWidths.slice();
  colWidths.splice(at, 0, ...Array.from({ length: count }, () => undefined));
  return { ...sheet, rows, colWidths, filters: {}, pinnedCols: at < sheet.pinnedCols ? sheet.pinnedCols + count : sheet.pinnedCols };
}

export function deleteCols(sheet: SheetData, at: number, count: number): SheetData {
  const rows = sheet.rows.map((row) => {
    if (row.length <= at) return row;
    const next = row.slice();
    next.splice(at, count);
    return next;
  });
  const colWidths = sheet.colWidths.slice();
  colWidths.splice(at, count);
  const pinned = at < sheet.pinnedCols ? Math.max(at, sheet.pinnedCols - count) : sheet.pinnedCols;
  return { ...sheet, rows, colWidths, filters: {}, pinnedCols: pinned };
}

export function setColWidth(sheet: SheetData, c: number, width: number): SheetData {
  const colWidths = sheet.colWidths.slice();
  for (let i = colWidths.length; i < c; i++) colWidths[i] = colWidths[i] ?? undefined;
  colWidths[c] = Math.max(MIN_COL_W, Math.min(MAX_COL_W, Math.round(width)));
  return { ...sheet, colWidths };
}

/** Autofit from content: ~7px per char of the longest value (sampled), clamped. */
export function autofitColWidth(sheet: SheetData, c: number): SheetData {
  let longest = colLabel(c).length;
  const sample = Math.min(sheet.rows.length, 2_000);
  for (let r = 0; r < sample; r++) {
    const len = getCell(sheet, r, c).length;
    if (len > longest) longest = len;
  }
  return setColWidth(sheet, c, Math.min(longest * 7 + 20, 380));
}

/** Autofit every data column at once (used right after parsing). */
export function autofitAll(sheet: SheetData): SheetData {
  let out = sheet;
  const cols = dataColCount(sheet);
  for (let c = 0; c < cols; c++) out = autofitColWidth(out, c);
  return out;
}

/** Sort data rows by a column (header row pinned out when frozenHeader). Numeric-aware. */
export function sortByColumn(sheet: SheetData, c: number, dir: 'asc' | 'desc'): SheetData {
  const start = sheet.frozenHeader ? 1 : 0;
  const head = sheet.rows.slice(0, start);
  const body = sheet.rows.slice(start);
  const keyed = body.map((row, i) => ({ row, i, v: row[c] ?? '' }));
  keyed.sort((a, b) => {
    // Blanks sink to the bottom in BOTH directions (Excel behavior).
    const ae = a.v === '',
      be = b.v === '';
    if (ae || be) return ae && be ? a.i - b.i : ae ? 1 : -1;
    const cmp = compareCells(a.v, b.v);
    return (dir === 'asc' ? cmp : -cmp) || a.i - b.i; // stable
  });
  return { ...sheet, rows: [...head, ...keyed.map((k) => k.row)] };
}

export function compareCells(a: string, b: string): number {
  const ae = a === '',
    be = b === '';
  if (ae || be) return ae && be ? 0 : ae ? 1 : -1;
  const an = Number(a),
    bn = Number(b);
  const aNum = a.trim() !== '' && !Number.isNaN(an);
  const bNum = b.trim() !== '' && !Number.isNaN(bn);
  if (aNum && bNum) return an - bn;
  if (aNum !== bNum) return aNum ? -1 : 1; // numbers before text, like Excel
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

/** Distinct values for the filter dropdown (capped; blanks shown as ''). */
export function columnValues(sheet: SheetData, c: number, cap = 1_000): { values: string[]; truncated: boolean } {
  const seen = new Set<string>();
  const start = sheet.frozenHeader ? 1 : 0;
  for (let r = start; r < sheet.rows.length; r++) {
    seen.add(getCell(sheet, r, c));
    if (seen.size > cap) return { values: [...seen].slice(0, cap).sort(compareCells), truncated: true };
  }
  return { values: [...seen].sort(compareCells), truncated: false };
}

// ── selection stats (status bar) ────────────────────────────────────────────

export interface SelectionStats {
  count: number;
  numeric: number;
  sum: number;
  avg: number;
  min: number;
  max: number;
}

export function selectionStats(sheet: SheetData, cells: Iterable<[number, number]>): SelectionStats {
  let count = 0,
    numeric = 0,
    sum = 0,
    min = Infinity,
    max = -Infinity;
  for (const [r, c] of cells) {
    const v = getCell(sheet, r, c);
    if (v === '') continue;
    count++;
    const n = Number(v.replace(/[$,%]/g, ''));
    if (!Number.isNaN(n) && v.trim() !== '') {
      numeric++;
      sum += n;
      if (n < min) min = n;
      if (n > max) max = n;
    }
  }
  return { count, numeric, sum, avg: numeric ? sum / numeric : 0, min, max };
}

// ── clipboard (Excel-interop TSV) ───────────────────────────────────────────

/** TSV/clipboard text → 2D grid. Honors Excel-style quoted cells (embedded \n / \t). */
export function parseClipboardGrid(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const s = text.endsWith('\r\n') ? text.slice(0, -2) : text.endsWith('\n') ? text.slice(0, -1) : text;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (quoted) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
    } else if (ch === '"' && cell === '') {
      quoted = true;
    } else if (ch === '\t') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && s[i + 1] === '\n') i++;
      row.push(cell);
      cell = '';
      rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  rows.push(row);
  return rows;
}

// ── cell decorations (validation overlays) ──────────────────────────────────

export type CellLevel = 'error' | 'warning' | 'info' | 'success';

export interface CellIssue {
  level: CellLevel;
  message: string;
}

export const cellKey = (r: number, c: number): string => `${r}:${c}`;

/** Row-level verdict shown in the gutter of an editable grid. */
export interface RowMark {
  level: CellLevel;
  label: string;
  message?: string;
}
